import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TooltipDirective } from '../../../shared/components/ui/tooltip/tooltip.directive';
import { FaDuotoneIconComponent, FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faSquarePlus,
  faXmark,
  faWandMagicSparkles,
  faPenToSquare,
  faCircleCheck,
  faLink,
} from '@fortawesome/pro-duotone-svg-icons';
import { faCopy, faCheck, faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { Button } from '../../../shared/components/ui/buttons/gradient-btn/button';
import { TextField } from '../../../shared/components/form-fields/text-field/text-field';
import { TextFieldAction } from '../../../shared/components/form-fields/text-field/text-field.types';
import { DatetimeField } from '../../../shared/components/form-fields/datetime-field/datetime-field';
import { generateRoomName } from './room-name-generator';
import {
  SelectField,
  SelectOption,
} from '../../../shared/components/form-fields/select-field/select-field';
import { MatToggleWrapper } from '../../../shared/components/material-wrappers/mat-toggle-wrapper/mat-toggle-wrapper';
import { SubmitDirective } from '../../../shared/directives/submit.directive';
import { E2eEncryptionPanel } from '../../pre-join/e2e-encryption-panel/e2e-encryption-panel';
import { RecurrenceFrequency, RoomSchedule } from '../../../shared/models/schedule';
import { Room } from '../../../state/rooms.repository';
import { RoomPasswordStore } from '../../../state/room-passwords.repository';
import { AppService } from '../../../core/services/app.service';

/**
 * Runs the server-side create for a submitted form. Resolves the new room's id,
 * or `null` when creation failed (the caller surfaces its own error). Supplying
 * it makes the dialog stay open and confirm the new room instead of closing —
 * callers that only collect form data (e.g. onboarding, where the instance does
 * not exist yet) simply omit it.
 */
export type CreateRoomHandler = (result: RoomDialogResult) => Promise<string | null>;

/**
 * What the dialog is for: creating a brand-new room, or editing an existing
 * one. Passed as `MAT_DIALOG_DATA`; an absent value means `'create'`.
 */
export type RoomDialogData =
  { mode: 'create'; create?: CreateRoomHandler } | { mode: 'edit'; room: Room };

export interface RoomDialogResult {
  roomName: string;
  isPrivate: boolean;
  /** The chosen meeting window, or `null` for "no schedule" / clear it. */
  schedule: RoomSchedule | null;
  /**
   * The new room's e2e password (`null` = encryption off). Always `null` when
   * editing: a room's encryption is fixed at creation.
   */
  password: string | null;
}

/** `id` is `'none'` (one-off/no repeat) or a `RecurrenceFrequency`. */
const FREQUENCY_OPTIONS: SelectOption[] = [
  { name: 'None', id: 'none' },
  { name: 'Daily', id: 'daily' },
  { name: 'Weekly', id: 'weekly' },
  { name: 'Monthly', id: 'monthly' },
];

/** How long the invite-link button shows its "copied" state. */
const COPY_FEEDBACK_MS = 1500;

/**
 * Modal form for creating or editing a room (room name, private toggle, e2e
 * panel, and an optional meeting schedule). In `'edit'` mode every control is
 * seeded from the passed room.
 *
 * The dialog does not talk to any server itself: it resolves the e2e password
 * locally and hands a {@link RoomDialogResult} to the caller — `RoomDialogService`
 * for both app flows, or the onboarding screen, which only collects room
 * definitions for a not-yet-provisioned instance.
 *
 * When the create caller supplies a {@link CreateRoomHandler}, the dialog runs it
 * on submit and stays open to confirm the new room: it shows the invite link
 * (which carries the e2e password as a `#p=` fragment) and the password itself,
 * which the creator would otherwise never see again.
 */
@Component({
  selector: 'andor-room-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    MatSlideToggle,
    TooltipDirective,
    FaDuotoneIconComponent,
    FaIconComponent,
    Button,
    TextField,
    SelectField,
    MatToggleWrapper,
    SubmitDirective,
    E2eEncryptionPanel,
    DatetimeField,
  ],
  templateUrl: './room-dialog.html',
  styleUrl: './room-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoomDialog {
  private readonly _dialogRef = inject<MatDialogRef<RoomDialog, RoomDialogResult>>(MatDialogRef);
  private readonly _translate = inject(TranslateService);
  private readonly _roomPasswords = inject(RoomPasswordStore);
  private readonly _app = inject(AppService);
  private readonly _clipboard = inject(Clipboard);
  private readonly _snackBar = inject(MatSnackBar);
  // MatDialog always provides the token, injecting `null` when the caller
  // passed no data — that (and the onboarding screen, which opens the dialog
  // without data) is the create case.
  private readonly _data: RoomDialogData = inject<RoomDialogData | null>(MAT_DIALOG_DATA) ?? {
    mode: 'create',
  };

  /** The room being edited, or `null` in create mode. */
  protected readonly room = this._data.mode === 'edit' ? this._data.room : null;
  protected readonly isEdit = this._data.mode === 'edit';
  private readonly _create = this._data.mode === 'create' ? (this._data.create ?? null) : null;

  protected readonly faSquarePlus = faSquarePlus;
  protected readonly faPenToSquare = faPenToSquare;
  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faLink = faLink;
  protected readonly faCopy = faCopy;
  protected readonly faCheck = faCheck;
  protected readonly faXmark = faXmark;

  readonly createNameControl = new FormControl(this.room?.name ?? '', {
    validators: [Validators.required, Validators.minLength(3)],
  });
  readonly isPrivate = signal(this.room?.isPrivate ?? false);

  // Inline "generate" button inside the room-name field: fills in a random
  // friendly name for users who'd rather not invent one. Re-rolls off the
  // current value so repeated taps always produce a different name.
  protected readonly nameGeneratorActions: TextFieldAction[] = [
    {
      icon: faWandMagicSparkles,
      tooltip: this._translate.instant('Generate a room name'),
      ariaLabel: this._translate.instant('Generate a room name'),
      click: (value) => {
        const name = generateRoomName(value);
        this.createNameControl.setValue(name);
        this.createNameControl.markAsDirty();
      },
    },
  ];

  // Schedule editor. Off by default when creating (→ no schedule, preserving
  // prior behavior); seeded from the room's stored schedule when editing.
  private readonly _schedule = this.room?.schedule ?? null;
  protected readonly scheduleEnabled = signal(this._schedule !== null);
  protected readonly frequencyOptions = FREQUENCY_OPTIONS;
  protected readonly selectedFrequency = signal<SelectOption>(
    FREQUENCY_OPTIONS.find((o) => o.id === this._schedule?.recurrence?.frequency) ??
      FREQUENCY_OPTIONS[0]
  );
  // Start/end as epoch ms (the picker's value type); null until chosen.
  protected readonly startMs = signal<number | null>(this._schedule?.startTime ?? null);
  protected readonly endMs = signal<number | null>(this._schedule?.endTime ?? null);
  // Captured once at open; the picker re-stamps its own "now" per open. Used to
  // disable past dates on Start (and, via startMs, on End). A stored schedule
  // may already have started, and the picker must not reject the value it was
  // seeded with — so editing lowers the floor to that start.
  protected readonly nowMs = Date.now();
  protected readonly startMin = Math.min(this.nowMs, this._schedule?.startTime ?? this.nowMs);
  // End can't precede Start; before a Start is picked, the floor stands in.
  protected readonly endMin = computed(() => this.startMs() ?? this.startMin);
  // The pickers work in the browser's zone; saving stamps it on the schedule so
  // recurrences keep this wall-clock time across daylight saving changes (and
  // re-saving a schedule stored without a zone upgrades it).
  protected readonly timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  protected readonly intervalControl = new FormControl(
    String(this._schedule?.recurrence?.interval ?? 1)
  );

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      if (this._copyResetTimeout) clearTimeout(this._copyResetTimeout);
    });

    // Default End to 30 minutes after Start whenever Start moves ahead of (or
    // past) the current End — gives a sensible window without forcing a manual
    // second pick. Reading endMs untracked keeps this from self-triggering.
    effect(() => {
      const start = this.startMs();
      if (start == null) return;
      const end = untracked(() => this.endMs());
      if (end == null || end <= start) {
        this.endMs.set(start + 30 * 60 * 1000);
      }
    });
  }

  // Singular/plural unit label for the "every N <unit>" row.
  protected readonly intervalUnitKey = computed(() => {
    switch (this.selectedFrequency().id) {
      case 'daily':
        return 'day(s)';
      case 'weekly':
        return 'week(s)';
      case 'monthly':
        return 'month(s)';
      default:
        return '';
    }
  });

  private readonly _encryption = viewChild(E2eEncryptionPanel);

  // --- confirmation step (create only) ------------------------------------
  /** Set once the room exists server-side; swaps the form for its confirmation. */
  protected readonly createdRoomId = signal<string | null>(null);
  protected readonly createdName = signal('');
  /** True while the create handler is in flight, so submit can't fire twice. */
  protected readonly submitting = signal(false);
  protected readonly passwordVisible = signal(false);
  protected readonly copied = signal(false);
  private _copyResetTimeout: ReturnType<typeof setTimeout> | null = null;

  // The link carries the e2e password as a `#p=` fragment, resolved from
  // `RoomPasswordStore` by the same builder the in-room share menu uses.
  protected readonly inviteLink = computed(() => {
    const roomId = this.createdRoomId();
    return roomId ? this._app.buildInviteLink(roomId) : '';
  });

  /** The new room's e2e key, or `null` when it was created unencrypted. */
  protected readonly createdPassword = computed(() => {
    const roomId = this.createdRoomId();
    return roomId ? (this._roomPasswords.get(roomId) ?? null) : null;
  });

  // The confirmation shows the password through a read-only text field, which
  // renders its value from a bound control (the component has no value input).
  protected readonly passwordControl = new FormControl('');

  protected readonly passwordActions = computed<TextFieldAction[]>(() => {
    const visible = this.passwordVisible();
    return [
      {
        icon: visible ? faEyeSlash : faEye,
        tooltip: this._translate.instant(visible ? 'Hide password' : 'Show password'),
        ariaLabel: this._translate.instant(visible ? 'Hide password' : 'Show password'),
        click: () => this.passwordVisible.set(!visible),
      },
      {
        icon: faCopy,
        tooltip: this._translate.instant('Copy password'),
        ariaLabel: this._translate.instant('Copy password'),
        click: (value) => this._copyPassword(value),
      },
    ];
  });

  // Submitting needs a valid room name, a non-blocking e2e state (encryption
  // enabled with an empty password blocks; editing shows it read-only), and —
  // when scheduling — a valid start/end window.
  protected submitDisabled(): boolean {
    if (this.submitting()) return true;
    if (this.createNameControl.invalid) return true;
    if (!this.isEdit && this._encryption()?.joinBlocked()) return true;
    if (this.scheduleEnabled() && !this._validScheduleWindow()) return true;
    return false;
  }

  protected async submit(): Promise<void> {
    if (this.submitDisabled()) return;
    const result: RoomDialogResult = {
      roomName: (this.createNameControl.value ?? '').trim(),
      isPrivate: this.isPrivate(),
      schedule: this._buildSchedule(),
      password: this.isEdit ? null : (this._encryption()?.resolveCreatePassword() ?? null),
    };

    // Without a create handler the dialog is a pure form: hand the result over
    // and let the caller take it from here.
    if (!this._create) {
      this._dialogRef.close(result);
      return;
    }

    this.submitting.set(true);
    const roomId = await this._create(result);
    this.submitting.set(false);
    // A failed create keeps the form (and its generated password) intact so the
    // user can retry — the caller has already surfaced the error.
    if (!roomId) return;

    this.createdName.set(result.roomName);
    this.passwordControl.setValue(this._roomPasswords.get(roomId) ?? '');
    this.createdRoomId.set(roomId);
  }

  protected close(): void {
    this._dialogRef.close();
  }

  /** Copy the invite link, flipping the button to its "copied" state briefly. */
  protected copyInviteLink(): void {
    const link = this.inviteLink();
    if (!link) return;
    this._clipboard.copy(link);
    this.copied.set(true);
    if (this._copyResetTimeout) clearTimeout(this._copyResetTimeout);
    this._copyResetTimeout = setTimeout(() => this.copied.set(false), COPY_FEEDBACK_MS);
  }

  private _copyPassword(value: string): void {
    if (!value) return;
    this._clipboard.copy(value);
    this._snackBar.open(this._translate.instant('Password copied to clipboard.'), undefined, {
      duration: 2500,
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }

  private _validScheduleWindow(): boolean {
    const start = this.startMs();
    const end = this.endMs();
    if (start == null || end == null) return false;
    return end > start;
  }

  private _buildSchedule(): RoomSchedule | null {
    if (!this.scheduleEnabled() || !this._validScheduleWindow()) return null;
    const startTime = this.startMs() as number;
    const endTime = this.endMs() as number;
    const freq = this.selectedFrequency().id;
    const recurrence =
      freq === 'none'
        ? undefined
        : {
            frequency: freq as RecurrenceFrequency,
            interval: Math.max(1, Math.floor(Number(this.intervalControl.value ?? '1') || 1)),
          };
    return { startTime, endTime, recurrence, timeZone: this.timeZone };
  }
}
